import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormField, TextInput } from "../ui/FormField";

const schema = z
  .object({
    current_password: z.string().min(1, "Please enter your current password"),
    new_password: z.string().min(8, "New password must be at least 8 characters"),
    confirm_password: z.string().min(1, "Please confirm your new password"),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });

type FormValues = z.infer<typeof schema>;

export interface ChangePasswordFormValues {
  current_password: string;
  new_password: string;
}

interface Props {
  onSave: (values: ChangePasswordFormValues) => void;
  onCancel: () => void;
  isPending?: boolean;
}

const ChangePasswordForm = ({ onSave, onCancel, isPending = false }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = (data: FormValues) => {
    onSave({ current_password: data.current_password, new_password: data.new_password });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-5"
    >
      <FormField label="Current Password" error={errors.current_password?.message}>
        <TextInput type="password" {...register("current_password")} placeholder="Enter current password" />
      </FormField>

      <FormField label="New Password" error={errors.new_password?.message}>
        <TextInput type="password" {...register("new_password")} placeholder="Enter new password" />
      </FormField>

      <FormField label="Confirm New Password" error={errors.confirm_password?.message}>
        <TextInput type="password" {...register("confirm_password")} placeholder="Confirm new password" />
      </FormField>

      <div className="flex flex-col gap-3 pt-2">
        <button
          type="submit"
          disabled={isPending}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isPending ? "Saving…" : "Change Password"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
